import React, { Component } from 'react'
import { bool, func } from 'prop-types'

import SearchSVG from 'components/atoms/SVGIcons/Search'
import ResetSVG from 'components/atoms/SVGIcons/Reset'
import { Input } from 'components/qstrap'
import { SlideDown } from 'components/atoms/Transitions'
import SearchAutocomplete from 'components/organisms/SearchAutocomplete'

import { lockScroll, unlockScroll } from 'utils/bodyScrollLock'
import API from 'utils/API'
import isDOMAvailable from 'utils/isDOMAvailable'
import { getSearchUrl, SelectedItemCtx } from 'utils/searchUtils'
import csrfToken from 'utils/csrfToken'
import axios from 'axios'
import { throttle, flatMap } from 'lodash'
import memoize from 'memoize-one'
import triggerEvent from 'utils/dataLayer'

class NewSearch extends Component {
  // initial state
  state = {
    autocompleteActive: false,
    autocompletePosition: 0,
    searchKeyword: '',
    searchResults: [],
    selectedItemId: null,
    cursorIdxPosition: -1,
    isLoading: false,
    isNoResults: false,
  }

  throttledFetchResults = throttle(() => this.fetchResults(), 1000)

  // flatten search results, for handling keyboard interaction
  flattenSearchResults = memoize(
    results => flatMap(results, resultGroup => resultGroup.children)
  )

  componentDidUpdate(prevProps, prevState) {
    const { displayAutocomplete, sideEffect } = this.props
    const { autocompleteActive } = this.state
    if (prevState.autocompleteActive !== autocompleteActive) {
      if (isDOMAvailable) {
        if (autocompleteActive) {
          document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`)
          lockScroll()
        } else {
          unlockScroll()
        }
      }
      if (autocompleteActive !== displayAutocomplete) {
        sideEffect()
      }
    }
  }

  // handling displayAutocomplete prop change,
  // since it's also related with mobile menu
  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.displayAutocomplete !== this.props.displayAutocomplete) {
      this.setState({
        autocompleteActive: this.props.displayAutocomplete,
      })
    }
    return null
  }

  displayAutocomplete = () => {
    if (isDOMAvailable) {
      const autocompletePosition = document.querySelector('.navbar--new__content').offsetHeight
      this.setState({
        autocompleteActive: true,
        autocompletePosition,
      }, this.fetchResults)
    }
  }

  fetchResults = () => {
    axios.get(API.endPoints().search.autocomplete, {
      params: {
        q: this.state.searchKeyword,
        group: true,
      },
    })
    .then((response) => {
      this.setState({
        isLoading: false,
        searchResults: response.data.results,
        isNoResults: !!(response.data.results.length < 1),
      })
      if (this.state.searchKeyword.length > 5) {
        triggerEvent('view_search_results', {
          search_term: this.state.searchKeyword,
          click_path: window.location.pathname
        })
      }
    })
    .catch(err => this.setState({
      isNoResults: err.response.status,
    }))
  }

  resetAutocomplete = () => {
    this.setState({ searchKeyword: '' })
  }

  hideAutocomplete = () => {
    this.setState({ autocompleteActive: false })
  }

  selectItemId = () => {
    const { searchResults, cursorIdxPosition } = this.state
    const flattenResults = this.flattenSearchResults(searchResults)
    const selectedItemId = flattenResults[cursorIdxPosition] && flattenResults[cursorIdxPosition].id
    this.setState({
      selectedItemId,
    })
  }

  // close autocomplete when esc pressed
  handleKeyup = (e) => {
    e.preventDefault()
    const { cursorIdxPosition, selectedItemId } = this.state
    const maxIdxOfResultItems = document.querySelectorAll('.search__results-item').length - 1
    switch (e.keyCode) {
      case 13: // handle enter
        if (selectedItemId) {
          this.handleSelect(selectedItemId)
        }
        return null
      case 27: // handle esc
        this.hideAutocomplete()
        return null
      case 38: // handle arrow up
        if (cursorIdxPosition > 0) {
          this.setState({
            cursorIdxPosition: cursorIdxPosition - 1,
          }, this.selectItemId)
        }
        return null
      case 40: // handle arrow down
        if (cursorIdxPosition < maxIdxOfResultItems) {
          this.setState({
            cursorIdxPosition: cursorIdxPosition + 1,
          }, this.selectItemId)
        }
        return null
      default:
        return null
    }
  }

  handleSearch = (val) => {
    this.setState({
      searchKeyword: val,
      isLoading: true,
      cursorIdxPosition: -1,
    }, () => {
      // results will be fetch only when the keyword length is >= 2
      if (val.length >= 2) this.throttledFetchResults()
      else this.setState({ isLoading: false })
    })
  }

  handleSelect = (value) => {
    const url = getSearchUrl(value)
    axios({
      url: API.endPoints().search.found,
      method: 'post',
      dataType: 'json',
      data: {
        url,
        keyword: this.state.searchKeyword,
      },
      headers: {
        'X-CSRF-Token': csrfToken(),
      },
    })
    .then(() => {
      window.location = url
    })
  }

  render() {
    const {
      autocompleteActive,
      autocompletePosition,
      searchKeyword,
      searchResults,
      selectedItemId,
      isLoading,
      isNoResults,
    } = this.state
    return (
      <div className="search">
        <div className="search__form">
          <Input
            type="search"
            placeholder="Cari di Quipper Campus.."
            onChange={e => this.handleSearch(e.target.value)}
            onFocus={this.displayAutocomplete}
            onKeyUp={this.handleKeyup}
            value={searchKeyword}
          />
          <div className="search__icon-search" onClick={this.handleClearSearch}>
            <SearchSVG />
          </div>
          {
            autocompleteActive && searchKeyword.length > 1 &&
            <div className="search__icon-reset" onClick={this.resetAutocomplete}>
              <ResetSVG fill="#1a344f" />
            </div>
          }
        </div>
        <SlideDown timeout={250} active={autocompleteActive} maxHeight="100vh">
          <SelectedItemCtx.Provider
            value={{
              id: selectedItemId,
              handleSelect: this.handleSelect,
            }}
          >
            <SearchAutocomplete
              isLoading={isLoading}
              isNoResults={isNoResults}
              autocompletePosition={autocompletePosition}
              searchResults={searchResults}
              onSelectItem={this.handleSelect}
              keyword={searchKeyword}
              close={this.hideAutocomplete}
              isNewSearch
            />
          </SelectedItemCtx.Provider>
        </SlideDown>
        <style jsx>
          {`
            .search {
              display: flex;
              justify-content: center;
              flex-direction: column;
              flex-grow: 1;
              height: 100%;
              cursor: pointer;

              @media screen and (min-width: 768px) {
                position: relative;
                margin-right: 40px;
              }

              &__form {
                position: relative;
                width: 100%;
                :global(input) {
                  height: 32px;
                  border-radius: 10px;
                  border: none;
                  font-size: 14px;
                  padding: ${autocompleteActive ? '8px 32px' : '8px 8px 8px 32px'};
                  background-color: #eff3f6;
                  @media screen and (min-width: 768px) {
                    padding: 10px 16px;
                    height: 40px;
                    border-radius: 4px;
                    line-height: 16px;
                  }
                  &::placeholder {
                    color: #A3B4CA;
                    opacity: 1;
                  }
                  &:focus {
                    box-shadow: none;
                  }
                }
              }

              &__icon-search {
                position: absolute;
                top: 8px;
                left: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                :global(svg) {
                  fill: #7d92a9;
                }
                @media screen and (min-width: 768px) {
                  left: unset;
                  right: 4px;
                  top: 4px;
                  width: 32px;
                  height: 32px;
                  border-radius: 4px;
                  background-color: #00c2c9;
                  :global(svg) {
                    fill: #fff;
                  }
                }
              }

              &__icon-reset {
                position: absolute;
                top: 8px;
                right: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 16px;
                height: 16px;
                @media screen and (min-width: 768px) {
                  display: none;
                }
              }

              &__icon {
                position: absolute;
                top: 0;
                margin: 8px;
                padding: 0;
                width: 16px;
                height: 16px;
                
                display: flex;
                align-items: center;
                justify-content: center;
                :global(svg) {
                  fill: #7d92a9;
                }
                @media screen and (min-width: 768px) {
                  right: 5px;
                  width: 32px;
                  height: 32px;
  
                  border-radius: 4px;
                  background-color: #00C2C9;
                  :global(svg) {
                    fill: #fff;
                  }
                }
              }
            }
          `}
        </style>
      </div>
    )
  }
}

NewSearch.propTypes = {
  displayAutocomplete: bool,
  sideEffect: func,
}

export default NewSearch
